/**
 * Email Privacy Utility Functions
 * 
 * Provides utilities for masking and sanitizing email addresses
 * to comply with data protection policies.
 */

/**
 * Masks an email address for privacy protection
 * 
 * @param {string} email - The email address to mask
 * @param {Object} options - Masking options
 * @param {boolean} options.showDomain - Whether to show the domain (default: true)
 * @param {number} options.visibleChars - Number of visible characters before @ (default: 2)
 * @param {string} options.maskChar - Character to use for masking (default: '*')
 * @returns {string} - Masked email address
 * 
 * @example
 * maskEmail('user@example.com') // Returns: 'us***@example.com'
 * maskEmail('user@example.com', { showDomain: false }) // Returns: 'us***@***'
 * maskEmail('user@example.com', { visibleChars: 1 }) // Returns: 'u****@example.com'
 */
export const maskEmail = (email, options = {}) => {
  if (!email || typeof email !== 'string') {
    return '';
  }

  const {
    showDomain = true,
    visibleChars = 2,
    maskChar = '*'
  } = options;

  // Use a more secure regex pattern that avoids catastrophic backtracking
  // This pattern uses possessive quantifiers equivalent and limits backtracking
  const emailRegex = /^[^\s@]{1,64}@[^\s@]{1,253}\.[^\s@]{1,63}$/;
  if (!emailRegex.test(email)) {
    return email; // Return original if not a valid email format
  }

  const [localPart, domain] = email.split('@');
  
  if (localPart.length <= visibleChars) {
    // If local part is too short, mask all but first character
    const maskedLocal = localPart.charAt(0) + maskChar.repeat(Math.max(3, localPart.length - 1));
    return showDomain ? `${maskedLocal}@${domain}` : `${maskedLocal}@${maskChar.repeat(3)}`;
  }

  const visiblePart = localPart.substring(0, visibleChars);
  const maskedPart = maskChar.repeat(Math.max(3, localPart.length - visibleChars));
  const maskedLocal = visiblePart + maskedPart;

  if (showDomain) {
    return `${maskedLocal}@${domain}`;
  } else {
    return `${maskedLocal}@${maskChar.repeat(3)}`;
  }
};

/**
 * Removes or masks email addresses from text content
 * 
 * @param {string} text - Text content to sanitize
 * @param {Object} options - Sanitization options
 * @param {boolean} options.mask - Whether to mask emails (true) or remove them (false)
 * @param {string} options.replacement - Replacement text when removing emails
 * @returns {string} - Sanitized text
 */
export const sanitizeEmailsInText = (text, options = {}) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  const {
    mask = true,
    replacement = '[EMAIL_REDACTED]'
  } = options;

  // Use a ReDoS-safe regex with bounded quantifiers to prevent catastrophic backtracking
  const emailRegex = /\b[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9.-]{1,253}\.[A-Za-z]{2,63}\b/g;

  if (mask) {
    return text.replace(emailRegex, (email) => maskEmail(email));
  } else {
    return text.replace(emailRegex, replacement);
  }
};

/**
 * Validates if email disclosure is authorized based on business rules
 * 
 * @param {string} context - Context where email is being used (e.g., 'admin-panel', 'user-profile', 'public-display')
 * @param {Object} user - User object requesting the email
 * @param {Object} targetUser - User whose email is being accessed
 * @returns {boolean} - Whether email disclosure is authorized
 */
export const isEmailDisclosureAuthorized = (context, user, targetUser) => {
  // If no context provided, default to not authorized
  if (!context) {
    return false;
  }

  // Admin users can see emails in admin contexts
  if (context === 'admin-panel' && user?.role === 'admin') {
    return true;
  }

  // Users can see their own email in profile contexts
  if (context === 'user-profile' && user?.user_id === targetUser?.user_id) {
    return true;
  }

  // Email editing contexts require same user or admin
  if (context === 'edit-profile' && (user?.user_id === targetUser?.user_id || user?.role === 'admin')) {
    return true;
  }

  // Never show emails in public contexts
  if (context === 'public-display' || context === 'search-results' || context === 'comments') {
    return false;
  }

  // Default to not authorized
  return false;
};

/**
 * Sanitizes user object by masking or removing email based on authorization
 * 
 * @param {Object} userObj - User object to sanitize
 * @param {string} context - Context where user object is being used
 * @param {Object} requestingUser - User requesting the data
 * @returns {Object} - Sanitized user object
 */
export const sanitizeUserObject = (userObj, context, requestingUser) => {
  if (!userObj) {
    return userObj;
  }

  const sanitized = { ...userObj };

  if (sanitized.email) {
    const isAuthorized = isEmailDisclosureAuthorized(context, requestingUser, userObj);
    
    if (!isAuthorized) {
      if (context === 'public-display' || context === 'search-results') {
        // Remove email completely in public contexts
        delete sanitized.email;
      } else {
        // Mask email in other contexts
        sanitized.email = maskEmail(userObj.email);
      }
    }
  }

  return sanitized;
};

/**
 * Creates a logging-safe version of an object by removing/masking sensitive data
 * 
 * @param {Object} obj - Object to make logging-safe
 * @param {Array} sensitiveFields - Array of field names to sanitize (default includes email fields)
 * @returns {Object} - Logging-safe object
 */
export const createLoggingSafeObject = (obj, sensitiveFields = ['email', 'emailAddress', 'userEmail']) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const safe = { ...obj };

  sensitiveFields.forEach(field => {
    if (safe[field]) {
      safe[field] = '[REDACTED]';
    }
  });

  return safe;
};

/**
 * Wrapper for console.log that automatically sanitizes sensitive data
 * 
 * @param {string} message - Log message
 * @param {*} data - Data to log (will be sanitized)
 * @param {Array} sensitiveFields - Fields to sanitize
 */
export const secureLog = (message, data, sensitiveFields) => {
  if (process.env.NODE_ENV === 'development') {
    const safeData = typeof data === 'object' ? createLoggingSafeObject(data, sensitiveFields) : data;
    console.log(message, safeData);
  }
};

/**
 * Wrapper for console.error that automatically sanitizes sensitive data
 * 
 * @param {string} message - Error message
 * @param {*} error - Error data (will be sanitized)
 * @param {Array} sensitiveFields - Fields to sanitize
 */
export const secureError = (message, error, sensitiveFields) => {
  const safeError = typeof error === 'object' ? createLoggingSafeObject(error, sensitiveFields) : error;
  console.error(message, safeError);
};